import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import MuiButton from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { useDeferredValue, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AutoComplete,
  Checkbox,
  DatePicker,
  NumberField,
  RadioGroup,
  Select,
  Switch,
  TextField,
} from '../../components/Inputs';
import { getNewLoad } from '../../legacy-components/apiCalls';
import Modal from '../../components/Modal';
import Spinner from '../../components/Progress';
import Stepper from '../../components/Stepper/Stepper';
import Tooltip from '../../components/Tooltip';
import SimpleTable from '../../legacy-components/SimpleTable';
import './ComponentPlayground.css';
import { Button } from '../../components/Buttons';
import { IconButton } from '../../components/Buttons/IconButton';
import ContentPanel from '../../components/ContentPanel';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

const Section = ({ children, title }) => {
  return (
    <Paper elevation={5} className="section">
      <h4>{title}</h4>
      {children}
    </Paper>
  );
};

const ASYNC_PAGE_SIZE = 20;

const filterCountryOptions = (options, search) => {
  const term = search.trim().toLowerCase();
  if (!term) return options;

  return options.filter(
    (option) => option.data?.toLowerCase().includes(term) || option.id?.toString().toLowerCase().includes(term)
  );
};

const renderCountryOption = (props, option) => (
  <li {...props}>
    <div>
      <div>{option.data}</div>
      <div style={{ fontSize: '0.85rem', color: '#666' }}>{option.id}</div>
    </div>
  </li>
);

/** Demonstrates async search + infinite scroll using the country list API (no NeeNor admin permissions required). */
const AsyncAutoCompleteDemo = () => {
  const [value, setValue] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDeferredValue(searchInput);

  const countriesQuery = useQuery({
    queryKey: ['countrycodesNeenor'],
    queryFn: async () => (await getNewLoad('countrycodesNeenor')) ?? [],
  });

  const infiniteQuery = useInfiniteQuery({
    queryKey: ['playground-country-autocomplete', debouncedSearch, countriesQuery.dataUpdatedAt],
    queryFn: ({ pageParam = 0 }) => {
      const filtered = filterCountryOptions(countriesQuery.data ?? [], debouncedSearch);
      const items = filtered.slice(pageParam, pageParam + ASYNC_PAGE_SIZE);
      const nextOffset = pageParam + ASYNC_PAGE_SIZE < filtered.length ? pageParam + ASYNC_PAGE_SIZE : undefined;

      return { items, nextOffset };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled: countriesQuery.isSuccess,
  });

  const options = infiniteQuery.data?.pages.flatMap((page) => page.items) ?? [];

  const handleInputChange = (_event, inputValue, reason) => {
    if (reason === 'input') {
      setSearchInput(inputValue);
    }
  };

  const handleLoadMore = () => {
    if (infiniteQuery.hasNextPage && !infiniteQuery.isFetchingNextPage) {
      return infiniteQuery.fetchNextPage();
    }
  };

  return (
    <AutoComplete
      label="Async search + infinite scroll (country demo)"
      tooltip="Loads from countrycodesNeenor — same AutoComplete pattern as NeeNor on the load screen (which uses getNewLoadNeeNorDropdown with server-side paging)"
      value={value}
      onChange={(_event, newValue) => setValue(newValue)}
      options={options}
      loading={countriesQuery.isPending || (infiniteQuery.isFetching && !infiniteQuery.isFetchingNextPage)}
      loadingMore={infiniteQuery.isFetchingNextPage}
      hasMore={infiniteQuery.hasNextPage}
      onLoadMore={handleLoadMore}
      onInputChange={handleInputChange}
      disableClientFilter
      getOptionLabel={(option) => option.data}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      renderOption={renderCountryOption}
      noOptionsText={countriesQuery.isPending ? 'Loading…' : 'No countries found'}
    />
  );
};
const ComponentPlayground = () => {
  const navigate = useNavigate();

  //===== Text field state =====
  const [textFieldValue, setTextFieldValue] = useState('');
  const handleTextChange = (event) => {
    setTextFieldValue(event.target.value);
  };
  //===== END Text field state =====

  //===== Stepper state=====
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState([
    { title: 'Step 1', completed: false },
    { title: 'Step 2', completed: false },
    { title: "There's a warning here", completed: false, state: 'warning', tooltip: 'Some details here' },
    {
      title: "There's an error here",
      completed: false,
      state: 'error',
      tooltip: (
        <>
          Some error
          <br />
          over two lines
        </>
      ),
    },
    { title: 'step 3', completed: false },
  ]);
  const onStepClick = (step) => {
    setActiveStep(step);
  };
  const completeStep = () => {
    const updatedCurrentStep = { ...steps[activeStep], completed: !steps[activeStep].completed };
    setSteps((prev) => {
      const newSteps = [...prev];
      newSteps[activeStep] = updatedCurrentStep;
      return newSteps;
    });
  };
  //===== END Stepper state=====

  //===== Checkbox state=====
  const [indeterminate, setIndeterminate] = useState(true);
  //===== END Checkbox state=====

  //===== Select state=====
  const [selectValue, setSelectValue] = useState('');
  //===== END Select state=====

  //===== AutoComplete state=====
  const [autoCompleteValue, setAutoCompleteValue] = useState(null);
  const staticAutoCompleteOptions = [
    { id: 1, label: 'Option one' },
    { id: 2, label: 'Option two' },
    { id: 3, label: 'Option three' },
  ];
  //===== END AutoComplete state=====

  //===== Modal state=====
  const modalTypes = Object.freeze({
    basic: 'basic',
    tariff: 'tariff',
    subtask: 'subtask',
    error: 'error',
  });
  const [modalType, setModalType] = useState('');
  const tableData = {
    headings: [
      {
        label: 'Third Country Duty (Ad Valorem)',
        key: 'thirdCountryDutyAdValorem',
        hide: false,
      },
      {
        label: 'Third Country Duty (Specific)',
        key: 'thirdCountryDutySpecific',
        hide: false,
      },
      {
        label: 'Duty - preferential rate',
        key: 'duty',
        hide: false,
      },
      {
        label: 'Quotas',
        key: 'quotas',
        hide: false,
      },
      {
        label: 'Trade remedies, safeguards and retaliatory duty',
        key: 'remedies',
        hide: false,
      },
      {
        label: 'VAT Rate',
        key: 'vat',
        hide: false,
      },
      {
        label: 'Excise',
        key: 'excise',
        hide: false,
      },
      {
        label: 'Customs Tech',
        key: 'customsTech',
        hide: false,
      },
    ],
    values: [
      {
        thirdCountryDutyAdValorem: '8.00 %',
        thirdCountryDutySpecific: '-',
        duty: '0.00 %',
        quotas: '-',
        remedies: '-',
        vat: 'Value added tax (20.0%)',
        excise: '-',
        customsTech: '-',
      },
    ],
  };
  //===== END Modal state=====

  if (process.env.REACT_APP_ENV === 'prod') {
    navigate('/main');
    return null;
  }

  return (
    <div className="component-playground">
      <Section title="Info tooltip">
        <Tooltip title="Demo Tooltip">This content has a tooltip</Tooltip>
        <Tooltip title="This has an 'i' icon" />
      </Section>
      <Section title="Text Field">
        <TextField label="Basic input" />
        <TextField
          label="With tooltip and text transform"
          value={textFieldValue}
          onChange={handleTextChange}
          tooltip={'Test tooltip in input field'}
          onBlur={() => setTextFieldValue(textFieldValue.toUpperCase())}
          sx={{ input: { textTransform: 'uppercase' } }}
        />
        <TextField helperText={'This one has an error and default'} defaultValue={'hunter2'} error />
      </Section>
      <Section title="Select">
        <Select
          label="Basic select"
          value={selectValue}
          onChange={(event) => setSelectValue(event.target.value)}
          options={[
            { label: 'Option one', value: 'one' },
            { label: 'Option two', value: 'two' },
            { label: 'Disabled option', value: 'disabled', disabled: true },
          ]}
        />
        <Select
          label="With error"
          value=""
          options={[{ label: 'Choose one', value: 'a' }]}
          error
          helperText="Please select a value"
        />
        <Select
          label="With tooltip"
          value={selectValue}
          onChange={(event) => setSelectValue(event.target.value)}
          tooltip="Help text for this select"
          options={[
            { label: 'Option one', value: 'one' },
            { label: 'Option two', value: 'two' },
          ]}
        />
      </Section>
      <Section title="AutoComplete">
        <AutoComplete
          label="Basic autocomplete"
          value={autoCompleteValue}
          onChange={(_event, newValue) => setAutoCompleteValue(newValue)}
          options={staticAutoCompleteOptions}
          getOptionLabel={(option) => option.label}
        />
        <AsyncAutoCompleteDemo />
      </Section>
      <Section title="Loading Spinner">
        <Spinner size="sm" />
        <Spinner size="md" />
        <Spinner size="lg" />
      </Section>
      <Section title="Switch">
        <Switch label="This is a switch" />
      </Section>
      <Section title="Radio Group">
        <RadioGroup
          label="Colours"
          buttons={[
            {
              label: 'Red',
              value: 'red',
            },
            {
              label: 'Green',
              value: 'green',
            },
            {
              label: 'Blue',
              value: 'blue',
            },
          ]}
        />
        <RadioGroup
          label="Colours but sideways"
          direction="horizontal"
          buttons={[
            {
              label: 'Red',
              value: 'red',
            },
            {
              label: 'Green',
              value: 'green',
            },
            {
              label: 'Blue',
              value: 'blue',
            },
          ]}
        />
      </Section>
      <Section title="Dates">
        <DatePicker label={'label'} />
        <DatePicker label={'With an error'} error helperText={'And helper text'} />
      </Section>
      <Section title="Stepper">
        <Stepper steps={steps} onStepClick={onStepClick} activeStep={activeStep} />
        <MuiButton onClick={completeStep} sx={{ width: 'fit-content' }}>
          {steps[activeStep].completed ? 'Un-complete current step' : 'Complete current step'}
        </MuiButton>
        <br />
        <br />
        <Stepper
          activeStep={2}
          steps={[
            { title: 'Vertical', completed: true },
            { title: 'And the steps', completed: true },
            { title: "Don't don't change", completed: false },
            { title: 'When clicked', completed: false },
          ]}
          direction="vertical"
        />
      </Section>
      <Section title="Checkboxes">
        <Checkbox label="Test" />
        <Checkbox label="Default to checked" defaultChecked />
        <Checkbox
          label="Secondary colour & indeterminate"
          color="secondary"
          indeterminate={indeterminate}
          onClick={() => setIndeterminate((prev) => !prev)}
        />
      </Section>
      <Section title="Modal">
        <MuiButton onClick={() => setModalType(modalTypes.basic)} sx={{ width: 'fit-content' }}>
          confirmation modal
        </MuiButton>
        <MuiButton onClick={() => setModalType(modalTypes.error)} sx={{ width: 'fit-content' }}>
          Error modal
        </MuiButton>
        <MuiButton onClick={() => setModalType(modalTypes.tariff)} sx={{ width: 'fit-content' }}>
          Tariff modal
        </MuiButton>
        <MuiButton onClick={() => setModalType(modalTypes.subtask)} sx={{ width: 'fit-content' }}>
          Subtask modal
        </MuiButton>
        <Modal
          title="PORTAL8675309"
          open={modalType === modalTypes.basic}
          onClose={() => setModalType('')}
          onConfirm={() => {
            alert('Confirmed');
            setModalType('');
          }}
          maxWidth="xs"
        >
          Please confirm you want to cancel this load
        </Modal>
        <Modal
          title="Unsaved changes"
          open={modalType === modalTypes.error}
          onClose={() => setModalType('')}
          onConfirm={() => setModalType('')}
          titleIcon={
            <ReportProblemIcon
              sx={(theme) => ({
                mr: theme.spacing(2),
                mb: '3px',
                color: 'warning.main',
              })}
            />
          }
          maxWidth="xs"
          showCloseButton
        >
          Unsaved changes
        </Modal>
        <Modal
          title="Tariff requirements: 030791"
          open={modalType === modalTypes.tariff}
          onConfirm={() => setModalType('')}
          onClose={() => setModalType('')}
          primaryButton={{ text: 'OK' }}
          secondaryButton={{ hidden: true }}
        >
          <div className="d-flex w-100 justify-content-between">
            <div>Country of Origin: fr</div>
            <div>Line Item: 2</div>
          </div>

          <div className="tariff-modal-sections">
            <ul className="w-100">
              <li style={{ marginLeft: '0rem' }}>18: Cocoa and cocoa preparations</li>
              <li style={{ marginLeft: '1rem' }}>1806: Chocolate and other food preparations containing cocoa</li>
            </ul>
          </div>

          <div className="tariff-modal-section-heading">
            <span>Measure Type</span>
            <span>Identified for this country</span>
          </div>

          <SimpleTable data={tableData} isHeaderRow={false} textAlign="right" />
        </Modal>
        <Modal
          title="Add subtask"
          open={modalType === modalTypes.subtask}
          onClose={() => setModalType('')}
          onConfirm={() => setModalType('')}
          maxWidth="xs"
        >
          <RadioGroup
            buttons={[
              { label: 'EXS', value: 'EXS' },
              { label: 'TAD', value: 'TAD' },
              { label: 'ENS', value: 'ENS' },
              { label: 'CHED', value: 'CHED' },
              { label: 'IPAFFS', value: 'IPAFFS' },
              { label: 'PHILIS', value: 'PHILIS' },
              { label: 'GMR', value: 'GMR' },
              { label: 'PBN', value: 'PBN' },
              { label: 'ADMIN FEE', value: 'FEE' },
            ]}
          />
        </Modal>
      </Section>

      <Section title="Buttons">
        <div
          className="w-100"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(9, minmax(0, 1fr))',
            gap: '12px',
            alignItems: 'center',
            justifyContent: 'center',
            justifyItems: 'center',
          }}
        >
          <Button>Primary</Button>

          <Button variant="secondary">Secondary</Button>

          <Button variant="text">Text button</Button>

          <Button color="success">Success</Button>

          <Button color="error">Error</Button>

          <Button variant="secondary" color="success">
            Secondary Success
          </Button>

          <Button variant="secondary" color="error">
            Secondary Error
          </Button>

          <Button
            variant="secondary"
            color="purple"
            disabled
            sx={{ '&.Mui-disabled:hover': { pointerEvents: 'auto', cursor: 'not-allowed' } }}
          >
            Disabled
          </Button>

          <Button startIcon={<CheckCircleIcon />}>With icon</Button>

          <Button variant="secondary" endIcon={<ReportProblemIcon />}>
            With icon
          </Button>
          <Button variant="text" color="red" startIcon={<ErrorIcon />}>
            With icon
          </Button>

          <Button loading loadingPosition="start">
            Primary Button Loading
          </Button>

          <Button loading loadingPosition="start" variant="secondary">
            Secondary Button Loading
          </Button>

          <IconButton icon={<DeleteIcon />} tooltip="Delete" />
          <IconButton icon={<DeleteIcon />} color="error" tooltip="Delete" />
          <IconButton icon={<SaveIcon />} disabled tooltip="Cannot save" />
          <IconButton icon={<SaveIcon />} color="primary" tooltip="Save" />
        </div>
      </Section>

      <Section title="Number Field">
        <div className="w-100 d-flex flex-column justify-content-center align-items-start gap-3">
          <NumberField label="Number Field" min={10} max={40} tooltip="Enter a number between 10 and 40" />
          <NumberField label="Number Field" />
          <NumberField
            label="Number Field with Error"
            min={10}
            max={40}
            defaultValue={100}
            tooltip="This is a tooltip"
            error
          />
        </div>
      </Section>

      <Section title="Content Panel">
        <ContentPanel maxWidth={500}>
          <Typography variant="h4" sx={{ color: '#033249', mb: 2, fontWeight: 500 }}>
            Taric Tool
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Commodity Code" placeholder="e.g. 030791" />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button color="primary">Find Now</Button>
            </Box>
            <Divider />
            <Box sx={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">Search results would appear here</Typography>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button>Cancel</Button>
            </Box>
          </Box>
        </ContentPanel>
      </Section>
    </div>
  );
};
export default ComponentPlayground;
